import React, { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import { useLocation } from '@reach/router';

import BurgerBtn from './components/BurgerBtn';
import MobileMenu from './components/MobileMenu';
import MainNav from './components/MainNav';
import LangSwitcher from './components/LangSwitcher/LangSwitcher';
import DesktopSearch from './components/DesktopSearch/DesktopSearch';
import Link from 'components/UI/Link';
import Button from 'components/UI/Button';
import Breadcrumbs from './components/Breadcrumbs';
import { HEADER_DEVICE_TYPE_BREAKPOINT } from 'define';
import { useGetDeviceType } from 'utils/hooks';
import { useHistoryCallback } from 'utils/routing';
import { isRequestedFrom, useLang, useTrans } from 'utils/localisation';
import { useHeaderTranslation } from 'staticQueries/header-translation';
import { arabicRegion, germanRegion, azerRegion } from 'define/locale';
import { pushHeaderEvent } from './utils';

import * as styles from './Header.module.scss';

const caseLayoutPages = ['/industries/financial-services/digital-banking-app'];

function Header({ is404 }) {
  const lang = useLang();
  const t = useTrans(['header']);
  const { links } = useHeaderTranslation();
  const currentLink = useLocation();
  const isIndexPage = currentLink.pathname === '/';
  const shouldBeWhite =
    currentLink.pathname.includes('/blueprint/') ||
    currentLink.pathname.includes('/project-cases/') ||
    currentLink.pathname.includes('/search') ||
    caseLayoutPages.includes(currentLink.pathname);
  const showProgressIndicator = currentLink.pathname.includes('/blueprint');
  const [headerFixed, setHeaderFixed] = useState(true);
  const [mobileMenuVisible, setActiveMobileMenu] = useState(false);
  const [enableSwitcher, setEnableSwitcher] = useState(false);
  const deviceType = useGetDeviceType(HEADER_DEVICE_TYPE_BREAKPOINT);
  const isMobileLayout = deviceType === 'Mobile';
  const shouldBeTransparent = (isIndexPage || !shouldBeWhite) && !headerFixed && isMobileLayout;
  const shouldBeFullLink = lang === 'az';
  const contactUsLink = shouldBeFullLink ? 'https://andersenlab.com/contact-us' : '/contact-us';
  const showBreadcrumbs = !isIndexPage && !shouldBeTransparent && !is404;

  const toggleMobileMenu = useCallback(() => {
    if (!mobileMenuVisible) {
      pushHeaderEvent('open_menu', null, 'Mobile');
    } else {
      pushHeaderEvent('close_menu', null, 'Mobile');
    }
    setActiveMobileMenu(!mobileMenuVisible);
  }, [mobileMenuVisible]);

  const handlerScroll = useCallback(() => {
    const scrollTop = (window.scrollY || document.scrollTop) - (document.clientTop || 0);

    scrollTop > 20 ? setHeaderFixed(true) : setHeaderFixed(false);
  }, []);

  const scrollProgress = () => {
    const scrollPx = document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = document.documentElement.clientHeight;

    const heightPx = scrollHeight - clientHeight;
    const scrolled = (scrollPx / heightPx) * 100;

    const progress = document.getElementById('progressBar');
    if (progress) {
      progress.style.width = `${scrolled}%`;
    }
  };

  useEffect(() => {
    if (
      lang === 'ae' ||
      lang === 'az' ||
      lang === 'de' ||
      isRequestedFrom(germanRegion) ||
      lang === 'fr' ||
      isRequestedFrom('FR') ||
      lang === 'nl' ||
      isRequestedFrom('NL')
    ) {
      setEnableSwitcher(true);
    }
    if (lang === 'en' && isIndexPage) {
      if (isRequestedFrom(arabicRegion) || isRequestedFrom(azerRegion)) {
        setEnableSwitcher(true);
      }
    }
    if (lang === 'en' && !isIndexPage) {
      if (isRequestedFrom(arabicRegion) || isRequestedFrom(azerRegion)) {
        setEnableSwitcher(false);
      }
    }
  }, [lang, isIndexPage]);

  useEffect(() => {
    handlerScroll();
    window.addEventListener('scroll', handlerScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handlerScroll);
    };
  }, []);

  useEffect(() => {
    if (showProgressIndicator) {
      window.addEventListener('scroll', scrollProgress, { passive: true });
      scrollProgress();
      return () => {
        window.removeEventListener('scroll', scrollProgress);
      };
    }
  }, [showProgressIndicator]);

  useHistoryCallback(() => {
    setActiveMobileMenu(false);
  });

  return (
    <header
      className={cn(
        styles.header,
        shouldBeTransparent && styles.transparentBg,
        shouldBeTransparent && !mobileMenuVisible && styles.whiteColor,
        'fixed-element',
      )}
    >
      <div className={styles.topLine}>
        <div className={styles.topLineLayout}>
          <Link
            href="/"
            className={cn(styles.logoWrap, mobileMenuVisible && styles.hidden)}
            onClick={() => {
              pushHeaderEvent('click_on_logo', null, deviceType);
            }}
          >
            <div className={styles.logo} aria-label="Andersen logo" role="img" />
          </Link>
          <div className={styles.mobilePanel}>
            <BurgerBtn
              fixed={mobileMenuVisible}
              active={mobileMenuVisible}
              theme={!mobileMenuVisible && shouldBeTransparent ? 'light' : null}
              onClick={toggleMobileMenu}
            />
            {isMobileLayout && (
              <MobileMenu
                links={links}
                open={mobileMenuVisible}
                enableSwitcher={enableSwitcher}
                contactUsLink={contactUsLink}
                setActiveMobileMenu={setActiveMobileMenu}
              />
            )}
          </div>
          {!isMobileLayout && (
            <nav className={styles.navigation}>
              <MainNav items={links} currentLink={currentLink.pathname} isIndexPage={isIndexPage} />
              <div className={styles.contactUsWrapper}>
                {enableSwitcher && (
                  <div className={styles.item}>
                    <LangSwitcher isIndexPage={isIndexPage} />
                  </div>
                )}
                {lang === 'en' && <DesktopSearch />}
                <Button
                  tag="a"
                  size="sm"
                  href={contactUsLink}
                  theme="header"
                  onClick={() => pushHeaderEvent('click_on_button', '/contact-us', 'Desktop')}
                >
                  {t('contactUsButton')}
                </Button>
              </div>
            </nav>
          )}
        </div>
      </div>
      <Breadcrumbs visible={showBreadcrumbs} />
      {showProgressIndicator && (
        <div className={styles.progressContainer}>
          <div className={styles.progressbar} id="progressBar" />
        </div>
      )}
    </header>
  );
}

export default Header;
